<template>

  <div id="newBooking" :class="{desktop: $store.state.isDesktop}">
    <div class="panel">

      <div class="title" v-if="$store.state.isDesktop">{{$t('booking.new')}}</div>

      <div class="form" v-show="!payment">
        <div class="left-desktop">
          <p>{{$t("booking.first_step_description")}}</p>

          <label for="selService" v-if="$store.state.isDesktop">{{ $t("booking.service.label") }}</label>
          <select id="selService" v-model="service" :class="{invalid: $validate.error('service')}">
            <option :value="null">{{$t('booking.service.empty')}}...</option>
            <option v-for="service of services" :value="service.id">{{$to(service.name)}}</option>
          </select>
          <label for="selService" v-if="!$store.state.isDesktop">{{ $t("booking.service.label") }}</label>

          <template v-if="!!service && !isUnitAboveAllowedBookings && !isUserAboveAllowedBookings">
            <template v-if="!showAvailableSchedulesForDate">
              <label for="selSchedule" v-if="$store.state.isDesktop">{{ $t("booking.schedule.label") }}</label>
              <select id="selSchedule" v-model="entity" :class="{invalid: $validate.error('entity')}" :disabled="!service">
                <option :value="null">{{$t('booking.schedule.empty')}}...</option>
                <option v-for="schedule of schedules" :value="schedule">{{$to(schedule.name)}}</option>
                <option v-for="eventGroup of eventGroups" :value="eventGroup">{{$to(eventGroup.name)}}</option>
              </select>
              <label for="selSchedule" v-if="!$store.state.isDesktop">{{ $t("booking.schedule.label") }}</label>
            </template>

            <label v-if="$store.state.isDesktop">{{ $t("booking.date") }}</label>
            <datetime id="txtDate" zone="UTC" v-model="date" value-zone="America/New_York" :format="DateTime.DATE_MED"
                      :input-class="{invalid: $validate.error('date')}"
              :disabled="!entity && !showAvailableSchedulesForDate" :min-datetime="new Date().toISOString()"></datetime>
            <label for="txtDate">
              <span v-if="!$store.state.isDesktop">{{ $t("booking.date") }}</span>
              <img class="icon" src="@/assets/img/icons/calendar_2.png" :class="{disabled: !entity}">
            </label>

            <div class="hr" v-if="!$store.state.isDesktop"></div>

            <div class="gridLegend">
              <label>
                <svgicon src="svg/info.svg" :height="14" :width="14"></svgicon>
                {{$t('booking.legend.description')}}
              </label>
              <div class="legend">
                <div class="hour">7:30</div>
                <div class="description">{{$t('booking.legend.available')}}</div>
              </div>
              <div class="legend">
                <div class="hour disabled">7:30</div>
                <div class="description">{{$t('booking.legend.unavailable')}}</div>
              </div>
              <div class="legend">
                <div class="hour selected">7:30</div>
                <div class="description">{{$t('booking.legend.selected')}}</div>
              </div>
            </div>

            <div class="hr" v-if="$store.state.isDesktop && price > 0"></div>

            <div class="price" v-if="$store.state.isDesktop && price > 0">
              <img src="@/assets/img/icons/bill.png" class="icon">
              <div class="right">
                <label>{{$t("booking.price")}}</label>
                <div class="value">{{price.toFixed(2)}} $</div>
              </div>
            </div>

            <div class="hr" v-if="schedule"></div>

            <div class="time-property" v-if="schedule">
              <img src="@/assets/img/icons/hours.png" class="icon">
              <div class="right" v-if="schedule.minimumDuration != schedule.maximumDuration">
                <label>{{$t('booking.required_time')}}</label>
                <div class="required-time">
                  <label class="min">{{$t('booking.min_time')}}:</label>
                  <span class="value" v-if="schedule.minimumDuration > 0">{{ formatTime(schedule.minimumDuration) }}</span>
                  <span class="value" v-else>N/A</span>
                  <div class="separator"></div>
                  <label class="max">{{$t('booking.max_time')}}:</label>
                  <span class="value" v-if="schedule.maximumDuration > 0">{{ formatTime(schedule.maximumDuration) }}</span>
                  <span class="value" v-else>N/A</span>
                </div>
              </div>
              <div class="right" v-if="schedule.minimumDuration == schedule.maximumDuration">
                <label>{{$t('booking.required_time_same')}}</label>
                <div class="required-time">
                  <span class="value" v-if="schedule.minimumDuration > 0">{{ formatTime(schedule.minimumDuration) }}</span>
                  <span class="value" v-else>N/A</span>
                </div>
              </div>
            </div>
          </template>

        </div>

        <div class="hr" v-if="!$store.state.isDesktop"></div>

        <div class="right-desktop" v-if="canDisplayDateSelection && !isUserAboveAllowedBookings && !isUnitAboveAllowedBookings">
          <p>{{$t('booking.choose')}}</p>

          <div class="change-date" v-show="canDisplayDateSelection && date">
            <button class="btn btn-neutral" @click="previousDate" :class="{disabled: isPreviousDateDisabled, invisible: isPreviousDateDisabled}">
              <svgicon class="prev" src="svg/arrow.svg" :height="16" :width="16"></svgicon>
            </button>
            <div class="date">{{DateTime.fromISO(date).toUTC().toLocaleString(DateTime.DATE_MED)}}</div>
            <button class="btn btn-neutral" @click="nextDate">
              <svgicon class="next" src="svg/arrow.svg" :height="16" :width="16"></svgicon>
            </button>
          </div>

          <div class="hr" v-show="canDisplayDateSelection && date"></div>

          <template v-if="!showAvailableSchedulesForDate && schedule">
            <GridTimePicker class="picker" :ref="'picker' + schedule.id" :times="times" :schedule="schedule" :date="date"
                            @sizeChanged="selectedTimeSize = $event" isSelected
                            :min="schedule.minimumDuration" :max="schedule.maximumDuration"></GridTimePicker>
          </template>

          <template v-if="!showAvailableSchedulesForDate && eventGroup">
            <div class="events">
              <div class="event" v-for="(availableEvent, eventIndex) of eventGroup.getEventsForDate(date)"
                   :key="'event-' + eventIndex" @click="selectEvent(availableEvent)"
                   :class="{disabled: typeof availableEvent.remainingAvailable !== 'undefined' && availableEvent.remainingAvailable <= 0,
                            selected: event === availableEvent}">
                <div class="left">
                  <div class="name">{{$to(availableEvent.name)}}</div>
                  <div class="places" v-if="availableEvent.maxAvailable">{{$t('booking.remaining')}}: {{availableEvent.remainingAvailable}}</div>
                </div>
                <div class="hour">{{getTime(availableEvent.start)}} - {{getTime(availableEvent.end)}}</div>
              </div>
            </div>
            <div class="none" v-if="eventGroup.getEventsForDate(date).length == 0">{{ $t("booking.none") }}</div>
          </template>

          <template v-if="showAvailableSchedulesForDate">
            <div class="schedule-grid" v-for="(availableSchedule, scheduleIndex) of schedules" :key="'schedule-' + availableSchedule.id">
              <div class="title">
                {{$to(availableSchedule.name)}}
              </div>
              <GridTimePicker class="picker" :ref="'picker' + availableSchedule.id" :times="timesForSchedule(availableSchedule)"
                              :schedule="availableSchedule" :date="date" @sizeChanged="selectedTimeSize = $event"
                              :isSelected="!!schedule && availableSchedule.id == schedule.id" @selected="changeSelectedSchedule(availableSchedule)"
                              :min="availableSchedule.minimumDuration" :max="availableSchedule.maximumDuration"></GridTimePicker>
            </div>
            <div class="schedule-grid" v-for="(availableEventGroup, eventGroupIndex) of eventGroups" :key="'eventgroup-' + eventGroupIndex"
                 v-if="availableEventGroup.getEventsForDate(date).length > 0">
              <div class="title">
                {{$to(availableEventGroup.name)}}
              </div>
              <div class="description" v-if="$to(availableEventGroup.description)">
                {{$to(availableEventGroup.description)}}
              </div>
              <div class="events">
                <div class="event" v-for="(availableEvent, eventIndex) of availableEventGroup.getEventsForDate(date)"
                     :class="{disabled: typeof availableEvent.remainingAvailable !== 'undefined' && availableEvent.remainingAvailable <= 0,
                              selected: event === availableEvent}"
                     @click="selectEvent(availableEvent)" :key="'event-' + eventIndex">
                  <div class="left">
                    <div class="name">{{$to(availableEvent.name)}}</div>
                    <div class="places" v-if="availableEvent.maxAvailable">{{$t('booking.remaining')}}: {{availableEvent.remainingAvailable}}</div>
                  </div>
                  <div class="hour">{{getTime(availableEvent.start)}} - {{getTime(availableEvent.end)}}</div>
                </div>
              </div>
            </div>
          </template>

          <div class="hr" v-if="!$store.state.isDesktop"></div>

          <div class="price" v-if="price > 0 && !$store.state.isDesktop">
            <img src="@/assets/img/icons/bill.png" class="icon">
            <div class="right">
              <label>{{$t("booking.price")}}</label>
              <div class="value">{{price.toFixed(2)}} $</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="service && isUserAboveAllowedBookings">
        <br>
        <p class="title" v-html="$t('booking.above_title')"></p>
        <p v-html="$t('booking.above_max').replace('{QTY}', maxAllowedBookingsPerCustomer)"></p>
      </div>

      <div class="restriction" v-if="(schedule || eventGroup) && service && maxAllowedBookingsPerCustomer && !isUserAboveAllowedBookings">
        <img src="@/assets/img/icons/info.png" class="icon">
        <div class="right">
          <label v-html="$t('booking.max_allowed').replace('{QTY}', maxAllowedBookingsPerCustomer)"></label>
        </div>
      </div>

      <div v-if="service && !isUserAboveAllowedBookings && isUnitAboveAllowedBookings">
        <br>
        <p class="title" v-html="$t('booking.above_title')"></p>
        <p v-html="$t('booking.above_max_unit').replace('{QTY}', maxAllowedBookingsPerUnit)"></p>
      </div>

      <div class="restriction" v-if="(schedule || eventGroup) && service && maxAllowedBookingsPerUnit && !isUnitAboveAllowedBookings">
        <img src="@/assets/img/icons/info.png" class="icon">
        <div class="right">
          <label v-html="$t('booking.max_allowed_unit').replace('{QTY}', maxAllowedBookingsPerUnit)"></label>
        </div>
      </div>

      <div class="buttons" v-show="!payment && !!service">
        <div class="btn btn-secondary" @click="back">{{ $t("navigation.back") }}</div>
        <div class="btn btn-primary" @click="send"
             v-if="!price && !isUserAboveAllowedBookings && !isUnitAboveAllowedBookings && (selectedTimeSize > 0 || (!!eventGroup && !!event))">
          {{ $t("booking.confirm") }}
        </div>
        <div class="btn btn-primary" @click="goToPayment"
             v-if="price > 0 && !isUserAboveAllowedBookings && !isUnitAboveAllowedBookings && (selectedTimeSize > 0 || (!!eventGroup && !!event))">
          {{ $t("booking.go_to_payment") }}
        </div>
      </div>

      <div class="form payment" v-show="payment">
        <p>{{$t("booking.second_step_description")}}</p>

        <div class="hr"></div>

        <div class="price" v-if="paymentAmount">
          <img src="@/assets/img/icons/bill.png" class="icon">
          <div class="right">
            <label>{{$t("booking.price")}}</label>
            <div class="value">{{paymentAmount.toFixed(2)}} $</div>
          </div>
        </div>

        <div class="hr"></div>

        <div class="stripe">
          <div v-show="Property.hasStripeEnabled()">
            <p>{{$t("booking.payment.enter_credit_card")}}</p>
            <div id="stripeElement"></div>
            <div class="powered">
              <img src="@/assets/img/powered_by_stripe_no_outline.png" alt="">
            </div>
          </div>
          <p v-show="!Property.hasPaymentEnabled()">{{$t('booking.payment.not_configured')}}</p>
        </div>

      </div>

      <div class="buttons" v-show="payment">
        <div class="btn btn-secondary" @click="backToSelection">{{ $t("navigation.back") }}</div>
        <div class="btn btn-primary" @click="pay" v-if="Property.hasPaymentEnabled() && !isUserAboveAllowedBookings && !isUnitAboveAllowedBookings">{{ $t("booking.confirm") }}</div>
      </div>

    </div>
  </div>

</template>

<script>

  import Services from '../../core/Services';
  import Schedules from "../../core/Schedules";
  import Reservations from "../../core/Reservations";
  import Schedule from "../../core/Schedules";
  import EventGroups from "../../core/EventGroups";
  import { DateTime } from "luxon";
  import moment from 'moment';
  import GridTimePicker from "../components/GridTimePicker";
  import Property from "../../core/Property";
  import user from "../../core/User";

  export default {

    name: "NewBooking",

    components: { GridTimePicker },

    data() {
      return {
        service: null,
        entity: null,
        event: null,
        date: moment().toISOString(),
        time: null,
        payment: false,
        paymentAmount: null,
        stripe: null,
        stripeCard: null,
        stripeError: null,
        stripeToken: null,
        selectedTimeSize: 0,
        Services,
        DateTime,
        Property,
        Schedules,
        Reservations,
        EventGroups,
        moment
      }
    },

    async mounted() {
      this.$store.commit("setDisplay", {
        showHeader: true,
        showHeaderLeft: true,
        showFooter: true,
        headerTitle: "page.booking",
        headerIcon: "/svg/history.svg"
      });
      showSpinner();
      await Services.init();
      if (!Schedules.initialized) {
        await Schedules.init();
      }
      await EventGroups.init();
      await Reservations.init();
      if (this.$route.query.service) {
        this.service = this.$route.query.service;
      }
      this.initStripe();
      hideSpinner();
    },

    validate: {
      service: { required: true },
      entity: { required: true },
      date: { required: true }
    },

    watch: {
      service: function(newValue, oldValue) {
        if (newValue !== oldValue && oldValue !== null) {
          this.entity = null;
          this.event = null;
        }
      }
    },

    computed: {
      services() {
        let services = this.Services.services.filter(s =>
          !!this.Schedules.getForServiceAccordingToFare(s.id, Property.hasPaymentEnabled()) ||
          !!this.EventGroups.getForServiceAccordingToFare(s.id, Property.hasPaymentEnabled()));
        if (user.notSignedOnCurrentLease) { // Filtering out service that require that lease is signed
          return services.filter(s => !s.availableOnlyForUserSignedOnLease)
        }
        return services;
      },
      schedule() {
        if (this.entity && this.entity.entityType === "Schedule") {
          return this.entity;
        }
        return null;
      },
      eventGroup() {
        if (this.entity && this.entity.entityType === "EventGroup") {
          return this.entity;
        }
        return null;
      },
      showAvailableSchedulesForDate() {
        if (!this.service) {
          return false;
        }
        let service = Services.get(this.service);
        return service.showAvailableSchedulesForDate;
      },
      canDisplayDateSelection() {
        return !!this.service && (!!this.entity || this.showAvailableSchedulesForDate);
      },
      schedules() {
        if (!this.service) {
          return [];
        }
        return this.Schedules.getForServiceAccordingToFare(this.service, Property.hasPaymentEnabled());
      },
      eventGroups() {
        if (!this.service) {
          return [];
        }
        return this.EventGroups.getForServiceAccordingToFare(this.service, Property.hasPaymentEnabled());
      },
      times() {
        if (!this.schedule || !this.date) {
          return [];
        }
        return this.timesForSchedule(this.schedule);
      },
      servicePrice() {
        if (!this.schedule) {
          return 0;
        }
        return this.schedule.price;
      },
      isPreviousDateDisabled() {
        let date = new Date(this.date);
        let now = new Date();
        return date.getUTCFullYear() === now.getUTCFullYear()
          && date.getUTCMonth() === now.getUTCMonth()
          && date.getUTCDate() === now.getUTCDate();
      },
      price() {
        if (this.eventGroup && this.event) {
          return this.event.price;
        }
        return Math.max(this.selectedTimeSize, 1) * this.servicePrice;
      },
      maxAllowedBookingsPerCustomer() {
        if (!this.service) {
          return 0;
        }
        let service = Services.get(this.service);
        return (service && service.maxActiveBookingPerUser ? service.maxActiveBookingPerUser : 0);
      },
      maxAllowedBookingsPerUnit() {
        if (!this.service) {
          return 0;
        }
        let service = Services.get(this.service);
        return (service && service.maxActiveBookingPerUnit ? service.maxActiveBookingPerUnit : 0);
      },
      isUserAboveAllowedBookings() {
        if (!this.service) {
          return false;
        }
        let activeUserBookings = Reservations.getActiveBookingsForService(this.service);
        return (this.maxAllowedBookingsPerCustomer <= activeUserBookings.length) && this.maxAllowedBookingsPerCustomer;
      },
      formatTime() {
        return minutes => {
          let str = "";
          if (minutes >= 60) {
            str += Math.floor(minutes / 60) + "h ";
          }
          if (minutes % 60 != 0) {
            str += minutes % 60 + "m";
          }
          return str;
        }
      },
      isUnitAboveAllowedBookings() {
        if (!this.service) {
          return false;
        }
        let activeUserBookings = Reservations.getActiveBookingsForServiceForCurrentLease(this.service);
        return this.maxAllowedBookingsPerUnit && (this.maxAllowedBookingsPerUnit <= activeUserBookings.length);
      },
      unitCurrentBookings() {
        if (!this.service) {
          return false;
        }
        return Reservations.getActiveBookingsForServiceForCurrentLease(this.service);
        //return this.maxAllowedBookingsPerUnit && (this.maxAllowedBookingsPerUnit <= activeUserBookings.length);
      },
      isUserAllowedBookingForLease() {
        if (!this.service) {
          return false;
        }
        let service = Services.get(this.service);
        return (service && service.availableOnlyForUserSignedOnLease) ? !user.notSignedOnCurrentLease : true;
      },
    },

    methods: {
      back() {
        window.history.back();
      },
      backToSelection() {
        this.payment = false;
        this.paymentAmount = null;
      },
      getPicker() {
        if (!this.schedule) {
          return;
        }
        let reference = this.$refs['picker' + this.schedule.id];
        return Array.isArray(reference) ? reference[0] : reference;
      },
      selectEvent(event) {
        if (typeof event.remainingAvailable !== 'undefined' && event.remainingAvailable <= 0) {
          return;
        }
        if (this.event === event) {
          this.event = null;
        } else {
          this.event = event;
          this.entity = EventGroups.get(this.event.group.id);
        }
        for (let key in this.$refs) {
          let reference = this.$refs[key];
          let ref = Array.isArray(reference) ? reference[0] : reference;
          if (typeof ref !== "undefined" && typeof ref.clearSelected !== "undefined") {
            ref.clearSelected();
          }
        }
      },
      changeSelectedSchedule(newSchedule) {
        this.event = null;
        if (this.schedule && this.schedule.id == newSchedule.id) {
          return;
        }
        this.entity = newSchedule;
      },
      getTime(date) {
        return date.format("HH:mm");
      },
      timesForSchedule(schedule) {
        if (!this.date) {
          return [];
        }
        let today = schedule.times.get(moment.utc(this.date).format("YYYYMMDD"));
        if (typeof today === "undefined" || today === null) {
          today = [];
        }
        return today;
      },
      async pay() {
        showSpinner();
        try {
          let response = await this.payWithStripe();
          if (response) {
            this.stripeToken = response.id;
          }
        } catch (e) {
          hideSpinner();
          console.log("ERROR STRIPE", e);
        }
        if (!this.stripeError && this.stripeToken) {
          hideSpinner();
          this.send();
        } else {
          hideSpinner();
          showAffirmation(this.$t("error"), this.$t("booking.error.invalid_card"));
        }
      },
      nextDate() {
        let date = new Date(this.date);
        date.setDate(date.getDate() + 1);
        this.date = date.toISOString();
      },
      previousDate() {
        let date = new Date(this.date);
        date.setDate(date.getDate() - 1);
        this.date = date.toISOString();
      },
      async send() {
        if (!this.$validate.valid()) {
          this.payment = false;
          return;
        }
        if (this.schedule) {
          this.sendWithSchedule();
        } else if (this.eventGroup && this.event) {
          this.sendWithEvent();
        }
      },
      async sendWithEvent() {
        try {
          showSpinner();
          let result = await this.event.createReservation(this.paymentAmount, this.stripeToken);
          hideSpinner();
          this.$router.push("/bookings");
        } catch (e) {
          showAffirmation(this.$t("error"), this.$to(e.response.data.message));
          hideSpinner();
        }
      },
      async sendWithSchedule() {
        showSpinner();
        let hours = this.getPicker().getSelected();
        if (!this.$validate.valid() || !hours || typeof hours == "string") {
          this.payment = false;
          document.getElementById("newBooking").scrollTo({
            top: 0,
            behavior: "smooth"
          });
          this.showHoursMessage(hours);
          hideSpinner();
          return;
        }
        try {
          let result = await this.schedule.createReservation(hours.start, hours.end, this.paymentAmount, this.stripeToken);
          hideSpinner();
          this.$router.push("/bookings");
        } catch (e) {
          showAffirmation(this.$t("error"), this.$to(e.response.data.message));
          hideSpinner();
        }
      },
      goToPayment() {
        if (this.schedule) {
          let hours = this.getPicker().getSelected();
          if (!this.$validate.valid() || !hours || typeof hours == "string") {
            this.showHoursMessage(hours);
            return;
          }
        } else if (!(this.eventGroup && this.event)) {
          return;
        }
        this.paymentAmount = this.price;
        this.payment = true;
      },
      initStripe() {
        if (Property.hasStripeEnabled()) {
          this.stripe = Stripe(Property.getPaymentPublicKey());
          let elements = this.stripe.elements();
          this.stripeCard = elements.create('card', {
            style: {
              fontSize: 16
            }
          });
          this.stripeCard.mount("#stripeElement");
          this.stripeCard.addEventListener("change", event => {
            if (event.error) {
              this.stripeError = event.error.code;
            } else {
              this.stripeError = null;
            }
          });
        }
      },
      payWithStripe() {
        return new Promise((resolve, reject) => {
          this.stripe.createToken(this.stripeCard)
            .then(result => {
              if (result.error) {
                reject(result);
              } else {
                resolve(result.token);
              }
            });
        });
      },
      showHoursMessage(type) {
        if (typeof type != "string") {
          return;
        }
        showAffirmation(this.$t("error"), this.$t("booking.error." + type).replace("{HOUR}", this.formatTime(this.schedule.minimumDuration)));
      }
    }
  }
</script>

<style lang="scss" scoped>

  #newBooking {
    background-color: #f2f3f4;
    padding: 20px;
    display: flex;
    flex-direction: column;
  }

  .form {
    flex-grow: 1;
  }

  .buttons {
    display: flex;
    margin-top: 20px;

    .btn {
      width: 100%;
    }

    .btn-secondary {
      margin-right: 20px;
    }
  }

  p {
    margin-top: 0;
    margin-bottom: 20px;
  }

  select, input {
    width: 100%;
  }

  .change-date {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;

    .prev {
      transform: rotate(180deg);
    }
  }

  .invisible {
    opacity: 0;
    visibility: hidden;
  }

  .price, .time-property, .restriction {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .right {
      padding-left: 10px;
    }

    .value {
      padding-left: 10px;
    }
  }
  .required-time {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;

    .max, .min {
      margin-top: 4px;
    }

    .max {
      margin-left: 0;
    }

    .separator {
      height: 2px;
      width: 8px;
      background: black;
      margin: 0 10px;
    }
  }

  .row {
    display: flex;
    & > div {
      flex-grow: 1;
      flex-basis: 0;
      &:not(:last-of-type) {
        margin-right: 14px;
      }
    }
  }

  .picker {
    margin-top: 2px;
    margin-bottom: 20px;
  }

  .desktop {

    .panel {
      margin: 30px;
      background-color: white;
      padding: 30px;

      .title {
        font-weight: bold;
        font-size: 18px;
        margin-bottom: 20px;
        padding-bottom: 5px;
        border-bottom: 1px solid #f2f3f4;
      }
    }

    .payment {
      flex-direction: column;
    }

    .form {
      display: flex;

      .left-desktop, .right-desktop {
        flex-grow: 1;
        flex-basis: 0;
      }
      .right-desktop {
        margin-left: 30px;
      }
    }

    select {
      margin-bottom: 10px;
    }

    label {
      font-size: 14px;
      margin-bottom: 4px;

      .icon {
        top: 12px;

        &.disabled {
          opacity: 0.4;
        }
      }
    }

    .buttons {
      justify-content: space-between;
      border-top: 1px solid #f2f3f4;
      padding-top: 20px;

      .btn {
        min-width:180px;
        width: auto;
      }
    }
  }

  .gridLegend {
    margin-top: 20px;
    margin-bottom: 20px;

    .legend {
      display: flex;
      align-items: center;
    }

    .description {
      margin-left: 15px;
      font-size: 14px;
    }

    label {
      margin-bottom: 8px !important;
      margin-left: 0 !important;
      display: flex;
      fill: #bfbfbf;
      align-items: center;

      .svgicon {
        margin-right: 8px;
      }
    }
  }

  .gridLegend .hour, .event {
    text-align: center;
    flex-shrink: 0;
    padding: 13px 0;
    width: 100px;
    font-weight: bold;
    color: #001722;
    background-color: #e1e4e6;
    border: 2px solid transparent;
    margin-bottom: -2px;
    margin-right: -2px;
    user-select: none;

    &.selected {
      position: relative;

      &::before, &::after {
        content: "";
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        height: 2px;
        background-color: #405159;
      }
      &::after {
        top: auto;
        bottom: -2px;
      }
      border: 2px solid #405159;
    }
    &.disabled {
      color: #bfc5c8;
      background-color: transparent;
      border-color: transparent;
    }
  }

  .desktop .gridLegend {
    .hour {
      padding: 10px 0;
      font-size: 14px;
    }
  }

  .event {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 13px 13px;
    cursor: pointer;

    &:not(:last-child) {
      margin-bottom: 5px;
    }
    .left {
      text-align: left;
    }
    .name {
      font-weight: 500;
      font-size: 14px;
    }
    .places {
      font-weight: normal;
      font-size: 12px;
    }
  }

  .stripe {
    max-width: 700px;
  }
  #stripeElement {
    border: 1px solid #d9d9d9;
    background-color: white;
    padding: 12px 10px;
  }
  .powered {
    margin-top: 5px;
    text-align: right;
    img {
      height: 16px;
    }
  }

  .right-desktop .schedule-grid {
    .title {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      padding-bottom: 5px;
      border-bottom: 1px solid #d9dcde;
      display: flex;

      .icon {
        margin-left: 10px;
      }
    }
  }

  .none {
    grid-column: span 5;
    text-align: center;
    font-weight: bold;
  }

</style>
